import type { AccessibleNode } from "../core/accessibility.js";
import type { Rect } from "../../common/geometry.js";
import type { AccessibleSnapshot, SnapshotEntry } from "./snapshot.js";

/**
 * One edit to make to the mirror.
 *
 * A list rather than a mutation, and that is the whole design. Everything
 * upstream of here is pure and testable because the decision about what to
 * change is separable from the act of changing it - so the assertions can be
 * about patches, in `environment: "node"`, instead of about a DOM that vitest
 * does not have and that jsdom would only pretend to have.
 */
export type MirrorPatch =
  | {
      op: "create";
      key: string;
      parentKey: string | null;
      /** Position among its siblings. */
      index: number;
      node: AccessibleNode;
      bounds: Rect;
      hidden: boolean;
    }
  | { op: "remove"; key: string }
  | { op: "move"; key: string; parentKey: string | null; index: number }
  | { op: "attrs"; key: string; node: AccessibleNode }
  | { op: "text"; key: string; text: string }
  | { op: "bounds"; key: string; bounds: Rect }
  | { op: "hidden"; key: string; hidden: boolean };

/**
 * What changed between two snapshots.
 *
 * Diffing beats observing, for this tree, for three reasons the framework
 * hands us and will not take back. There is no hook for a control leaving the
 * tree, so a departure can only be noticed as a key that stopped appearing.
 * `Control.invalidate` carries no identity - it sets one flag on the desktop -
 * so nothing says *which* control changed its text. And `ControlCollection`
 * reorders without running the attach lifecycle, on purpose, so a z-order
 * change tells nobody anything. A keyed diff catches all three and the
 * alternative catches none of them.
 *
 * Costs nothing when nothing moved: an unchanged pair of snapshots produces
 * an empty list, and the desktop does not even call this on a frame it did
 * not paint.
 */
export function reconcile(prev: AccessibleSnapshot, next: AccessibleSnapshot): MirrorPatch[] {
  const patches: MirrorPatch[] = [];
  const after = index(next);
  // Seats counted over the survivors only. Removing an element shuffles
  // everything after it up by one, and seats counted over the old list would
  // call every one of those a move - ten redundant moves for every row a long
  // list scrolls past, each one re-inserting an element a reader may be part
  // way through speaking.
  const before = index(prev.filter((e) => after.has(e.key)));

  const gone = new Set<string>();
  for (const entry of prev) if (!after.has(entry.key)) gone.add(entry.key);
  for (const entry of prev) {
    if (!gone.has(entry.key)) continue;
    // A container that goes takes its children with it. Emitting a remove per
    // descendant would have the applier hunt for elements the first remove
    // already detached.
    if (entry.parentKey !== null && gone.has(entry.parentKey)) continue;
    patches.push({ op: "remove", key: entry.key });
  }

  // `next` is in document order, so a parent is always described before the
  // children that have to be appended to it.
  const seats = new Map<string, number>();
  for (const entry of next) {
    const parent = entry.parentKey ?? "";
    const seat = seats.get(parent) ?? 0;
    seats.set(parent, seat + 1);

    const was = before.get(entry.key);
    if (!was) {
      patches.push({
        op: "create",
        key: entry.key,
        parentKey: entry.parentKey,
        index: seat,
        node: entry.node,
        bounds: entry.bounds,
        hidden: entry.hidden,
      });
      continue;
    }

    if (was.parentKey !== entry.parentKey || was.seat !== seat) {
      patches.push({ op: "move", key: entry.key, parentKey: entry.parentKey, index: seat });
    }
    if (!sameAttributes(was.entry.node, entry.node)) {
      patches.push({ op: "attrs", key: entry.key, node: entry.node });
    }
    if ((was.entry.node.text ?? "") !== (entry.node.text ?? "")) {
      patches.push({ op: "text", key: entry.key, text: entry.node.text ?? "" });
    }
    if (!sameRect(was.entry.bounds, entry.bounds)) {
      patches.push({ op: "bounds", key: entry.key, bounds: entry.bounds });
    }
    if (was.entry.hidden !== entry.hidden) {
      patches.push({ op: "hidden", key: entry.key, hidden: entry.hidden });
    }
  }

  return patches;
}

interface Seated {
  entry: SnapshotEntry;
  parentKey: string | null;
  seat: number;
}

function index(snapshot: AccessibleSnapshot): Map<string, Seated> {
  const seats = new Map<string, number>();
  const out = new Map<string, Seated>();
  for (const entry of snapshot) {
    const parent = entry.parentKey ?? "";
    const seat = seats.get(parent) ?? 0;
    seats.set(parent, seat + 1);
    out.set(entry.key, { entry, parentKey: entry.parentKey, seat });
  }
  return out;
}

/**
 * Everything about a node except its words.
 *
 * Text is split out because it is the thing that changes on its own - a clock
 * label, a status line - and rewriting a dozen ARIA attributes to change one
 * string is how a mirror starts interrupting a reader mid-sentence.
 */
function sameAttributes(a: AccessibleNode, b: AccessibleNode): boolean {
  const left = a as unknown as Record<string, unknown>;
  const right = b as unknown as Record<string, unknown>;
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  for (const key of keys) {
    if (key === "text") continue;
    if (left[key] !== right[key]) return false;
  }
  return true;
}

function sameRect(a: Rect, b: Rect): boolean {
  return a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;
}
